/**
 * "Train from past emails" — AI-09 §C2. Paste 3-10 emails → analyze →
 * review the detected profile → prefill the wizard. Analyze is disabled
 * below 3 filled boxes (the server re-validates). The Idempotency-Key
 * content hash makes a repeat Analyze of unchanged emails free.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../../lib/api";
import { analyzeVoice, type VoiceAnalysis } from "../../../lib/voiceAnalysis";
import { Button } from "../../ui/button";

const MAX_EMAILS = 10;
const MIN_EMAILS = 3;

export interface TrainPanelProps {
  kitId: string;
  onUse: (analysis: VoiceAnalysis) => void;
  onBack: () => void;
}

export function TrainPanel({ kitId, onUse, onBack }: TrainPanelProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [emails, setEmails] = useState<string[]>(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [analysis, setAnalysis] = useState<VoiceAnalysis | null>(null);

  const filled = emails.map((e) => e.trim()).filter((e) => e !== "");
  const canAnalyze = filled.length >= MIN_EMAILS && !loading;

  async function runAnalyze(): Promise<void> {
    setLoading(true);
    setError(null);
    setRetryable(false);
    try {
      const result = await analyzeVoice(kitId, filled, i18n.language);
      setAnalysis(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("brandVoice.train.errors.rate_limited"));
      } else if (err instanceof ApiError && err.status === 422) {
        setError(t("brandVoice.train.errors.invalid"));
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t("brandVoice.train.errors.forbidden"));
      } else {
        setError(t("brandVoice.train.errors.unavailable"));
        setRetryable(true);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">{t("brandVoice.train.intro")}</p>

      {emails.map((email, i) => (
        <textarea
          key={i}
          aria-label={t("brandVoice.train.email_label", { n: i + 1 })}
          value={email}
          rows={3}
          placeholder={t("brandVoice.train.email_placeholder", { n: i + 1 })}
          onChange={(e) =>
            setEmails(emails.map((v, j) => (j === i ? e.target.value : v)))
          }
          className="block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      ))}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={emails.length >= MAX_EMAILS}
          onClick={() => setEmails([...emails, ""])}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-neutral-300"
        >
          + {t("brandVoice.train.add_email")}
        </button>
        <span className="text-xs text-neutral-400">
          {t("brandVoice.train.count_hint", { filled: filled.length, min: MIN_EMAILS })}
        </span>
      </div>

      {error !== null && (
        <div role="alert" className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700">
          {error}
          {retryable && (
            <button type="button" onClick={() => void runAnalyze()} className="ml-2 font-medium underline">
              {t("common.retry")}
            </button>
          )}
        </div>
      )}

      {analysis === null ? (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            {t("brandVoice.back")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!canAnalyze}
            loading={loading}
            onClick={() => void runAnalyze()}
          >
            {t("brandVoice.train.analyze")}
          </Button>
        </div>
      ) : (
        <div className="space-y-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
          <p className="text-sm font-medium text-neutral-900">
            {t("brandVoice.train.detected_tone")}:{" "}
            {t(`brandVoice.tones.${analysis.suggestion.tone}.label`, analysis.suggestion.tone)}
            <span className="ml-2 font-normal text-neutral-500">
              {t("brandVoice.train.confidence", {
                percent: Math.round(analysis.confidence * 100),
              })}
            </span>
          </p>
          {analysis.suggestion.signature_phrases.length > 0 && (
            <p className="text-sm text-neutral-600">
              {t("brandVoice.train.common_phrases")}:{" "}
              {analysis.suggestion.signature_phrases.slice(0, 5).join("; ")}
            </p>
          )}
          {analysis.suggestion.tone_description !== "" && (
            <p className="text-sm italic text-neutral-600">
              {analysis.suggestion.tone_description}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="primary" size="sm" onClick={() => onUse(analysis)}>
              {t("brandVoice.train.use_settings")}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setAnalysis(null)}>
              {t("brandVoice.train.reanalyze")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
